import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import { videos, categories } from '@/mocks/videos';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'intro' | 'episodes'>('intro');

  const video = videos.find((v) => v.id === id);
  const category = video ? categories.find((c) => c.id === video.category) : null;
  const relatedVideos = video
    ? videos.filter((v) => v.category === video.category && v.id !== video.id).slice(0, 4)
    : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-800 mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该视频可能已被下架或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: video.title,
        description: video.description,
        image: video.thumbnail,
        datePublished: video.updateDate,
        author: { '@type': 'Organization', name: '果冻传媒' },
        publisher: { '@type': 'Organization', name: '果冻传媒' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.guodongmedia.com/' },
          { '@type': 'ListItem', position: 2, name: category?.name || '', item: `https://www.guodongmedia.com/category/${video.category}` },
          { '@type': 'ListItem', position: 3, name: video.title },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navbar />

      <main className="pt-16 pb-16">
        {/* Video Hero */}
        <section className="relative w-full aspect-video max-h-[600px] overflow-hidden">
          <img
            src={video.thumbnail}
            alt={video.title}
            title={`${video.title} - 果冻传媒高清视频`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/20 to-transparent"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-primary-500/90 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-all duration-200 transform hover:scale-105">
              <i className="ri-play-fill text-background-50 text-3xl md:text-4xl ml-1"></i>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-[1400px] mx-auto">
              <div className="flex items-center gap-2 text-xs text-background-50/70 mb-3">
                <span className="px-2 py-0.5 bg-primary-500 rounded text-background-50">{category?.name}</span>
                <span>{video.duration}</span>
                {video.episodes > 0 && <span>共{video.episodes}集</span>}
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-2">
                {video.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-background-50/70">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万次播放` : `${video.views}次播放`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  {video.rating} 分
                </span>
                <span>更新于 {video.updateDate}</span>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 mt-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${video.category}`} className="hover:text-primary-600 transition-colors cursor-pointer">
              {category?.name}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium truncate">{video.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Tab Switcher */}
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-6 w-fit">
                {[
                  { key: 'intro' as const, label: '简介' },
                  { key: 'episodes' as const, label: '选集' },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                      activeTab === tab.key
                        ? 'bg-background-50 text-foreground-950 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'intro' && (
                <div>
                  <div className="prose max-w-none">
                    <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                      {video.description}
                    </p>
                  </div>

                  {video.tags && video.tags.length > 0 && (
                    <div className="mt-6 flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-foreground-500">标签：</span>
                      {video.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'episodes' && (
                <div>
                  {video.episodes > 0 ? (
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                      {Array.from({ length: Math.min(video.episodes, 24) }, (_, i) => (
                        <button
                          key={i}
                          className={`aspect-video rounded-lg flex items-center justify-center text-sm font-medium cursor-pointer transition-all ${
                            i === 0
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                          }`}
                        >
                          第{i + 1}集
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-foreground-500">暂无选集信息</p>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar - Related */}
            <div>
              {relatedVideos.length > 0 && (
                <div>
                  <h3 className="font-heading font-bold text-foreground-900 mb-4">相关推荐</h3>
                  <div className="flex flex-col gap-4">
                    {relatedVideos.map((rv) => (
                      <Link key={rv.id} to={`/article/${rv.id}`} className="flex gap-3 group cursor-pointer">
                        <div className="w-36 sm:w-40 aspect-video rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={rv.thumbnail}
                            alt={rv.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                            {rv.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {rv.views >= 10000 ? `${(rv.views / 10000).toFixed(1)}万` : rv.views}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                              {rv.rating}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}